import {
  isMemberControllable,
  VISIBILITY_MODES,
  type ProfileQuestionDetail,
  type QuestionVisibilityPolicy,
  type ViewerContext,
  type VisibilityMode,
} from '@aysar/contracts';

/**
 * How exposed a mode leaves a field, for comparing the two contexts.
 *
 * `user_controlled` sits between the two forced modes because the member may go
 * either way — which is exactly why "matched is stricter than public" is a warning
 * rather than an error: with `user_controlled` on one side, whether it is really
 * stricter depends on a person.
 */
const OPENNESS: Readonly<Record<VisibilityMode, number>> = {
  forced_hidden: 0,
  user_controlled: 1,
  forced_visible: 2,
};

/**
 * The policy, gathered from a question **read**.
 *
 * The api flattens the four columns onto the question on the way out and nests them
 * under `visibility` on the way in. That asymmetry is real and deliberate on the
 * server (the columns live on the question, not in a policy table), so it is
 * reconciled here — once — rather than at every form.
 */
export function policyOf(question: {
  publicVisibility: VisibilityMode;
  matchedVisibility: VisibilityMode;
  defaultVisiblePublic: boolean;
  defaultVisibleMatched: boolean;
}): QuestionVisibilityPolicy {
  return {
    publicVisibility: question.publicVisibility,
    matchedVisibility: question.matchedVisibility,
    defaultVisiblePublic: question.defaultVisiblePublic,
    defaultVisibleMatched: question.defaultVisibleMatched,
  };
}

/** The policy a NEW question starts from: the member decides, and nothing leaks by default. */
export function defaultPolicy(): QuestionVisibilityPolicy {
  return {
    publicVisibility: 'user_controlled',
    matchedVisibility: 'user_controlled',
    defaultVisiblePublic: false,
    defaultVisibleMatched: true,
  };
}

/** The mode of one context, so a control can read and write it by name. */
export function modeFor(policy: QuestionVisibilityPolicy, context: ViewerContext): VisibilityMode {
  return context === 'matched' ? policy.matchedVisibility : policy.publicVisibility;
}

/** The member-facing default of one context. Meaningless unless the mode is user-controlled. */
export function defaultFor(policy: QuestionVisibilityPolicy, context: ViewerContext): boolean {
  return context === 'matched' ? policy.defaultVisibleMatched : policy.defaultVisiblePublic;
}

/** Whether the default toggle of a context does anything at all. */
export function isDefaultEditable(
  policy: QuestionVisibilityPolicy,
  context: ViewerContext,
): boolean {
  return isMemberControllable(policy, context);
}

export function withMode(
  policy: QuestionVisibilityPolicy,
  context: ViewerContext,
  mode: VisibilityMode,
): QuestionVisibilityPolicy {
  return context === 'matched'
    ? { ...policy, matchedVisibility: mode }
    : { ...policy, publicVisibility: mode };
}

export function withDefault(
  policy: QuestionVisibilityPolicy,
  context: ViewerContext,
  isVisible: boolean,
): QuestionVisibilityPolicy {
  return context === 'matched'
    ? { ...policy, defaultVisibleMatched: isVisible }
    : { ...policy, defaultVisiblePublic: isVisible };
}

/**
 * Whether the **matched** context is more restrictive than the **public** one.
 *
 * Almost always a mistake, and flagged rather than refused: `matched` is meant to
 * be a superset surface — whatever a stranger can see, someone she accepted can see
 * too. A field that is public but hidden after a match is a field that disappears
 * the moment two people match, which nobody sets out to configure.
 *
 * The `viewer-context` contract asks for this warning by name, and it stays a
 * warning because an operator may genuinely mean it (`03-visibility-policy.md`).
 */
export function isMatchedStricterThanPublic(policy: QuestionVisibilityPolicy): boolean {
  return OPENNESS[policy.matchedVisibility] < OPENNESS[policy.publicVisibility];
}

/**
 * The four shapes a policy's consequence can take, independent of which
 * context it is read for — exhaustive over `VisibilityMode × isDefaultVisible`
 * (the default only matters under `user_controlled`, so the forced modes
 * collapse to one outcome each regardless of it).
 */
export type VisibilityOutcomeKey =
  'forcedVisible' | 'forcedHidden' | 'userControlledVisible' | 'userControlledHidden';

/**
 * The plain-language CONSEQUENCE of one context's policy — what a matrix row
 * ends in, so an operator reads what happens rather than naming the mode a
 * second time. `03-visibility-policy.md`'s "plain language per mode" taken to
 * its end.
 */
export function outcomeKeyFor(
  policy: QuestionVisibilityPolicy,
  context: ViewerContext,
): VisibilityOutcomeKey {
  const mode = modeFor(policy, context);

  if (mode === 'user_controlled') {
    return defaultFor(policy, context) ? 'userControlledVisible' : 'userControlledHidden';
  }

  return mode === 'forced_visible' ? 'forcedVisible' : 'forcedHidden';
}

/** Every mode, in the order the matrix offers them: hidden → member → shown. */
export const MODE_ORDER: readonly VisibilityMode[] = [
  'forced_hidden',
  'user_controlled',
  'forced_visible',
];

/** A guard, so the constant above can never drift from the contract. */
export function allModesCovered(): boolean {
  return (
    MODE_ORDER.length === VISIBILITY_MODES.length &&
    VISIBILITY_MODES.every((mode) => MODE_ORDER.includes(mode))
  );
}

/**
 * A facet nobody named.
 *
 * The section-wide write takes a **patch**, so "leave this alone" is the absence
 * of a key — never a value. This constant exists only so a control can offer it
 * as a real choice; it is stripped on the way into the payload by construction,
 * because the patch never holds it.
 */
export const UNCHANGED = 'unchanged';

/** One context's mode in a section-wide patch: a mode, or nothing said. */
export type SectionModeChoice = typeof UNCHANGED | VisibilityMode;

/**
 * One context's starting position in a section-wide patch.
 *
 * Three states, not two — which is why this is a choice and not a switch: a
 * boolean cannot say "leave every question's own starting position where it is".
 */
export type SectionDefaultChoice = typeof UNCHANGED | 'visible' | 'hidden';

/** The body of `PUT /admin/questions/visibility/:sectionId`, minus its wrapper. */
export type SectionVisibilityPatch = Partial<QuestionVisibilityPolicy>;

/** Which column of the patch each context's mode lives in. */
const MODE_KEY = {
  public: 'publicVisibility',
  matched: 'matchedVisibility',
} as const satisfies Record<ViewerContext, keyof QuestionVisibilityPolicy>;

/** Which column of the patch each context's starting position lives in. */
const DEFAULT_KEY = {
  public: 'defaultVisiblePublic',
  matched: 'defaultVisibleMatched',
} as const satisfies Record<ViewerContext, keyof QuestionVisibilityPolicy>;

export function modeChoiceFor(
  patch: SectionVisibilityPatch,
  context: ViewerContext,
): SectionModeChoice {
  return patch[MODE_KEY[context]] ?? UNCHANGED;
}

export function defaultChoiceFor(
  patch: SectionVisibilityPatch,
  context: ViewerContext,
): SectionDefaultChoice {
  const stored = patch[DEFAULT_KEY[context]];

  if (stored === undefined) return UNCHANGED;
  return stored ? 'visible' : 'hidden';
}

/**
 * Names — or un-names — one context's mode.
 *
 * Choosing a **forced** mode drops that context's starting position as well: the
 * member is never asked under either forced mode, so the column would be written
 * for nobody. Dropping it here is what lets the control disable that group
 * without leaving a value on screen that is nonetheless still going to be sent.
 */
export function withSectionMode(
  patch: SectionVisibilityPatch,
  context: ViewerContext,
  choice: SectionModeChoice,
): SectionVisibilityPatch {
  const { [MODE_KEY[context]]: _mode, ...rest } = patch;

  if (choice === UNCHANGED) return rest;
  if (choice === 'user_controlled') return { ...rest, [MODE_KEY[context]]: choice };

  const { [DEFAULT_KEY[context]]: _default, ...withoutDefault } = rest;
  return { ...withoutDefault, [MODE_KEY[context]]: choice };
}

export function withSectionDefault(
  patch: SectionVisibilityPatch,
  context: ViewerContext,
  choice: SectionDefaultChoice,
): SectionVisibilityPatch {
  const { [DEFAULT_KEY[context]]: _default, ...rest } = patch;

  return choice === UNCHANGED ? rest : { ...rest, [DEFAULT_KEY[context]]: choice === 'visible' };
}

/**
 * Whether naming a starting position for this context would land anywhere.
 *
 * `false` only where the patch **explicitly** forces the mode. Deliberately
 * `true` while the mode is unchanged: the section's questions may hold mixed
 * modes, so the default still reaches every user-controlled one of them.
 */
export function isSectionDefaultMeaningful(
  patch: SectionVisibilityPatch,
  context: ViewerContext,
): boolean {
  const mode = modeChoiceFor(patch, context);

  return mode === UNCHANGED || mode === 'user_controlled';
}

/**
 * The consequence of one context of a **patch** — the four whole-policy outcomes
 * plus the three only a partial can produce.
 */
export type SectionOutcomeKey =
  | VisibilityOutcomeKey
  | typeof UNCHANGED
  | 'userControlledKept'
  | 'defaultOnlyVisible'
  | 'defaultOnlyHidden';

/**
 * The plain-language consequence of one context of a section-wide patch.
 *
 * `outcomeKeyFor`'s counterpart for a value where a facet may be absent — and
 * absence is the interesting half: a mode named without a starting position
 * leaves each question's own, and a starting position named without a mode
 * reaches only the questions whose member decides.
 */
export function sectionOutcomeKeyFor(
  patch: SectionVisibilityPatch,
  context: ViewerContext,
): SectionOutcomeKey {
  const mode = modeChoiceFor(patch, context);
  const start = defaultChoiceFor(patch, context);

  if (mode === 'forced_visible') return 'forcedVisible';
  if (mode === 'forced_hidden') return 'forcedHidden';

  if (mode === 'user_controlled') {
    if (start === UNCHANGED) return 'userControlledKept';
    return start === 'visible' ? 'userControlledVisible' : 'userControlledHidden';
  }

  if (start === UNCHANGED) return UNCHANGED;
  return start === 'visible' ? 'defaultOnlyVisible' : 'defaultOnlyHidden';
}

/**
 * The stricter-than-public warning, for a patch.
 *
 * Raised **only** when both modes are named. With one side unnamed there is
 * nothing to compare against — every question of the section keeps its own
 * stored mode there — so the comparison would be a guess, and a warning that
 * fires on a guess is one operators learn to dismiss.
 */
export function isSectionPatchStricterThanPublic(patch: SectionVisibilityPatch): boolean {
  const { publicVisibility, matchedVisibility } = patch;

  if (publicVisibility === undefined || matchedVisibility === undefined) return false;
  return OPENNESS[matchedVisibility] < OPENNESS[publicVisibility];
}

/**
 * The warning shown before a destructive edit — how many members already answered.
 *
 * A count, not a list: the builder needs "312 members have answered this" before
 * staff deactivate or delete a question, and nothing more (the QueryBus contract
 * behind it says the same).
 */
export function answeredCount(detail: ProfileQuestionDetail | undefined): number {
  return detail?.answerCount ?? 0;
}
